import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from "reactstrap";
import toastr from "toastr";
import * as Yup from "yup";
import {
  useCourseCreate,
  useCourseUpdate,
  useGetAllCourses,
  useToggleCourseStatus
} from "../../../api/courses";
import { useDebounce } from "../../../hooks/useDebounce";
import { resolvePaginatedList } from "../../../helpers/paginatedResponse";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";
import TableContainer from "../../../components/Common/TableContainer";

const IS_ACTIVE_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" }
];

const emptyProvider = () => ({ link: "", description: "" });

/** Normalize API / legacy course shape into form rows */
function providersFromCourse(course) {
  if (Array.isArray(course?.providers) && course.providers.length > 0) {
    return course.providers.map((p) => ({
      link: p?.link != null ? String(p.link) : "",
      description: p?.description != null ? String(p.description) : ""
    }));
  }
  if (course?.providerLink) {
    return [{ link: String(course.providerLink), description: "" }];
  }
  return [emptyProvider()];
}

function toPayloadProviders(items) {
  return items.map((p) => ({
    link: String(p?.link ?? "").trim(),
    description: String(p?.description ?? "").trim()
  }));
}

function providersListsEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every(
    (p, i) => p.link === b[i].link && p.description === b[i].description
  );
}

const courseValidationSchema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
  description: Yup.string().trim(),
  providers: Yup.array()
    .of(
      Yup.object({
        link: Yup.string()
          .trim()
          .required("Provider link is required")
          .url("Enter a valid URL (e.g. https://example.com)"),
        description: Yup.string().trim()
      })
    )
    .min(1, "Add at least one provider")
});

const CourseList = () => {
  document.title = "Courses | Admin";

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [isActiveFilter, setIsActiveFilter] = useState("all");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [pendingToggleSlug, setPendingToggleSlug] = useState(null);
  const [coursesState, setCoursesState] = useState({ list: [], total: 0 });

  const { mutate, data, isLoading, isPending, error } = useGetAllCourses();
  const createMutation = useCourseCreate();
  const updateMutation = useCourseUpdate();
  const toggleCourseStatus = useToggleCourseStatus();
  const { mutate: toggleMutate, isPending: togglePending } = toggleCourseStatus;
  const loading = isLoading || isPending;

  const buildListParams = useCallback(() => {
    const payload = { page, limit };
    if (isActiveFilter !== "all") {
      payload.isActive = isActiveFilter === "true";
    }
    const trimmed = debouncedSearch.trim();
    if (trimmed) {
      payload.search = trimmed;
    }
    return payload;
  }, [page, limit, isActiveFilter, debouncedSearch]);

  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({ page, limit, isActiveFilter, search: debouncedSearch });

  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    mutate(buildListParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  useEffect(() => {
    const { list, total: nextTotal } = resolvePaginatedList(data, {
      listKeys: ["data", "items", "courses"],
    });
    setCoursesState({ list, total: nextTotal });
  }, [data]);

  const courses = coursesState.list;
  const total = coursesState.total;

  const createFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      description: "",
      providers: [emptyProvider()],
    },
    validationSchema: courseValidationSchema,
    onSubmit: (values) => {
      createMutation.mutate(
        {
          name: values.name.trim(),
          description: values.description.trim(),
          providers: toPayloadProviders(values.providers),
        },
        {
          onSuccess: () => {
            toastr.success("Course created successfully!");
            setShowCreateModal(false);
            createFormik.resetForm();
            mutate(buildListParams());
          },
          onError: () => {},
        }
      );
    },
  });

  const updateFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selectedCourse?.name || "",
      description: selectedCourse?.description || "",
      providers: providersFromCourse(selectedCourse),
    },
    validationSchema: courseValidationSchema,
    onSubmit: (values) => {
      if (!selectedCourse?.slug) return;
      const payload = {};
      const n = values.name.trim();
      const desc = values.description.trim();
      const nextProviders = toPayloadProviders(values.providers);
      const origProviders = toPayloadProviders(
        providersFromCourse(selectedCourse)
      );

      if (n !== (selectedCourse.name || "").trim()) payload.name = n;
      if (desc !== (selectedCourse.description || "").trim())
        payload.description = desc;
      if (!providersListsEqual(origProviders, nextProviders)) {
        payload.providers = nextProviders;
      }
      if (Object.keys(payload).length === 0) {
        toastr.info("No changes to update");
        return;
      }
      updateMutation.mutate(
        { slug: selectedCourse.slug, ...payload },
        {
          onSuccess: () => {
            toastr.success("Course updated successfully!");
            setShowUpdateModal(false);
            setSelectedCourse(null);
            updateFormik.resetForm();
            mutate(buildListParams());
          },
          onError: () => {},
        }
      );
    },
  });

  useEffect(() => {
    if (selectedCourse) {
      updateFormik.setValues({
        name: selectedCourse.name || "",
        description: selectedCourse.description || "",
        providers: providersFromCourse(selectedCourse),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse]);

  const handleEdit = useCallback((course) => {
    setSelectedCourse(course);
    setShowUpdateModal(true);
  }, []);

  const handleToggleStatus = useCallback(
    (course, nextStatus) => {
      if (!course?.slug) return;
      setPendingToggleSlug(course.slug);
      toggleMutate(
        { slug: course.slug, status: nextStatus },
        {
          onSuccess: (res) => {
            const msg = res?.message;
            if (msg) toastr.success(msg);
            else
              toastr.success(
                `Course ${nextStatus ? "activated" : "deactivated"}`
              );
            setCoursesState((prev) => ({
              ...prev,
              list: prev.list.map((item) =>
                item?.slug === course.slug
                  ? { ...item, isActive: nextStatus }
                  : item
              ),
            }));
            mutate(buildListParams());
          },
          onSettled: () => setPendingToggleSlug(null),
        }
      );
    },
    [toggleMutate, mutate, buildListParams]
  );

  const refetchList = useCallback(() => {
    mutate(buildListParams());
  }, [mutate, buildListParams]);

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "-",
      },
      {
        header: "Slug",
        accessorKey: "slug",
        enableColumnFilter: false,
        cell: (info) => {
          const slug = info.getValue();
          if (!slug) return "-";
          return (
            <span
              style={{ maxWidth: "200px", display: "inline-block" }}
              title={slug}
            >
              {slug}
            </span>
          );
        },
      },
      {
        header: "Providers",
        id: "providers",
        enableColumnFilter: false,
        cell: (info) => {
          const row = info.row.original;
          const list =
            Array.isArray(row?.providers) && row.providers.length > 0
              ? row.providers
              : row?.providerLink
              ? [{ link: row.providerLink, description: "" }]
              : [];
          if (!list.length) return "-";
          return (
            <ul className="list-unstyled mb-0 small">
              {list.map((p, idx) => {
                const url = p?.link;
                if (!url) return <li key={idx}>-</li>;
                const label = url.length > 36 ? `${url.slice(0, 36)}…` : url;
                const tip = [p?.description, url].filter(Boolean).join(" — ");
                return (
                  <li key={idx}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={tip || undefined}
                    >
                      {label}
                    </a>
                  </li>
                );
              })}
            </ul>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: (info) => {
          const isActive = !!info.getValue();
          const course = info.row.original;
          const toggleId = `course-status-${course?.slug || info.row.id}`;
          const isRowPending =
            togglePending && pendingToggleSlug === course?.slug;
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="form-check form-switch mb-0">
                <Input
                  id={toggleId}
                  type="switch"
                  checked={isActive}
                  disabled={isRowPending}
                  onChange={() => handleToggleStatus(course, !isActive)}
                />
                <Label className="visually-hidden" for={toggleId}>
                  Toggle course status
                </Label>
              </div>
              {isRowPending && (
                <i className="mdi mdi-loading mdi-spin text-muted" />
              )}
            </div>
          );
        },
      },
      {
        header: "Created",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue()).toLocaleDateString()
            : "-",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const course = cellProps.row.original;
          return (
            <Button
              color="primary"
              size="sm"
              onClick={() => handleEdit(course)}
              disabled={updateMutation.isPending}
            >
              <i className="mdi mdi-pencil-outline me-1" />
              Edit
            </Button>
          );
        },
      },
    ],
    [
      handleEdit,
      handleToggleStatus,
      pendingToggleSlug,
      updateMutation.isPending,
      togglePending,
    ]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Courses" breadcrumbItem="Courses List" />

        <Row className="mb-3 g-2 align-items-center">
          <Col md={4}>
            <Input
              type="text"
              placeholder="Search courses…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </Col>
          <Col md={2}>
            <Input
              type="select"
              value={isActiveFilter}
              onChange={(e) => {
                setIsActiveFilter(e.target.value);
                setPage(1);
              }}
            >
              {IS_ACTIVE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </Col>
          <Col md="auto" className="d-flex align-items-end">
            <Button
              color="primary"
              onClick={() => setShowCreateModal(true)}
              disabled={loading}
            >
              <i className="mdi mdi-plus me-1" />
              Create course
            </Button>
          </Col>
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button
              color="secondary"
              outline
              onClick={() => {
                setSearch("");
                setIsActiveFilter("all");
                setLimit(10);
                setPage(1);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button
              color="light"
              onClick={() => refetchList()}
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && courses.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load courses.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={courses}
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />
                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={showCreateModal}
          toggle={() => setShowCreateModal(false)}
          centered
        >
          <ModalHeader toggle={() => setShowCreateModal(false)}>
            Create course
          </ModalHeader>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              createFormik.handleSubmit();
              return false;
            }}
          >
            <ModalBody>
              <div className="mb-3">
                <Label className="form-label">
                  Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="name"
                  placeholder="Course name"
                  value={createFormik.values.name}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    createFormik.touched.name && !!createFormik.errors.name
                  }
                />
                {createFormik.touched.name && createFormik.errors.name && (
                  <FormFeedback type="invalid">
                    {createFormik.errors.name}
                  </FormFeedback>
                )}
              </div>
              <div className="mb-3">
                <Label className="form-label">Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  rows={3}
                  placeholder="Main course description…"
                  value={createFormik.values.description}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  invalid={
                    createFormik.touched.description &&
                    !!createFormik.errors.description
                  }
                />
                {createFormik.touched.description &&
                  createFormik.errors.description && (
                    <FormFeedback type="invalid">
                      {createFormik.errors.description}
                    </FormFeedback>
                  )}
              </div>
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Label className="form-label mb-0">
                    Providers <span className="text-danger">*</span>
                  </Label>
                  <Button
                    type="button"
                    color="light"
                    size="sm"
                    onClick={() =>
                      createFormik.setFieldValue("providers", [
                        ...createFormik.values.providers,
                        emptyProvider(),
                      ])
                    }
                  >
                    <i className="mdi mdi-plus me-1" />
                    Add provider
                  </Button>
                </div>
                {typeof createFormik.errors.providers === "string" && (
                  <div className="text-danger small mb-2">
                    {createFormik.errors.providers}
                  </div>
                )}
                {createFormik.values.providers.map((row, index) => (
                  <Card key={index} className="mb-2 border">
                    <CardBody className="py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="text-muted small">
                          Provider {index + 1}
                        </span>
                        {createFormik.values.providers.length > 1 && (
                          <Button
                            type="button"
                            color="link"
                            className="text-danger p-0 small"
                            onClick={() => {
                              const next = createFormik.values.providers.filter(
                                (_, i) => i !== index
                              );
                              createFormik.setFieldValue(
                                "providers",
                                next.length ? next : [emptyProvider()]
                              );
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <div className="mb-2">
                        <Label className="form-label small mb-1">
                          Link <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="url"
                          name={`providers.${index}.link`}
                          placeholder="https://example.com"
                          value={row.link}
                          onChange={createFormik.handleChange}
                          onBlur={createFormik.handleBlur}
                          invalid={
                            createFormik.touched.providers?.[index]?.link &&
                            !!createFormik.errors.providers?.[index]?.link
                          }
                        />
                        {createFormik.touched.providers?.[index]?.link &&
                          createFormik.errors.providers?.[index]?.link && (
                            <FormFeedback type="invalid">
                              {createFormik.errors.providers[index].link}
                            </FormFeedback>
                          )}
                      </div>
                      <div className="mb-0">
                        <Label className="form-label small mb-1">
                          Provider description
                        </Label>
                        <Input
                          type="textarea"
                          name={`providers.${index}.description`}
                          rows={2}
                          placeholder="Optional"
                          value={row.description}
                          onChange={createFormik.handleChange}
                          onBlur={createFormik.handleBlur}
                          invalid={
                            createFormik.touched.providers?.[index]
                              ?.description &&
                            !!createFormik.errors.providers?.[index]
                              ?.description
                          }
                        />
                        {createFormik.touched.providers?.[index]?.description &&
                          createFormik.errors.providers?.[index]
                            ?.description && (
                            <FormFeedback type="invalid">
                              {createFormik.errors.providers[index].description}
                            </FormFeedback>
                          )}
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                color="light"
                type="button"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? "Saving…" : "Create"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <Modal
          isOpen={showUpdateModal}
          toggle={() => setShowUpdateModal(false)}
          centered
        >
          <ModalHeader toggle={() => setShowUpdateModal(false)}>
            Edit course
          </ModalHeader>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              updateFormik.handleSubmit();
              return false;
            }}
          >
            <ModalBody>
              {selectedCourse?.slug && (
                <div className="mb-3">
                  <Label className="form-label text-muted">Slug</Label>
                  <div>
                    <Badge color="light" className="text-dark">
                      {selectedCourse.slug}
                    </Badge>
                  </div>
                </div>
              )}
              <div className="mb-3">
                <Label className="form-label">
                  Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="name"
                  value={updateFormik.values.name}
                  onChange={updateFormik.handleChange}
                  onBlur={updateFormik.handleBlur}
                  invalid={
                    updateFormik.touched.name && !!updateFormik.errors.name
                  }
                />
                {updateFormik.touched.name && updateFormik.errors.name && (
                  <FormFeedback type="invalid">
                    {updateFormik.errors.name}
                  </FormFeedback>
                )}
              </div>
              <div className="mb-3">
                <Label className="form-label">Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  rows={3}
                  placeholder="Main course description…"
                  value={updateFormik.values.description}
                  onChange={updateFormik.handleChange}
                  onBlur={updateFormik.handleBlur}
                  invalid={
                    updateFormik.touched.description &&
                    !!updateFormik.errors.description
                  }
                />
                {updateFormik.touched.description &&
                  updateFormik.errors.description && (
                    <FormFeedback type="invalid">
                      {updateFormik.errors.description}
                    </FormFeedback>
                  )}
              </div>
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Label className="form-label mb-0">
                    Providers <span className="text-danger">*</span>
                  </Label>
                  <Button
                    type="button"
                    color="light"
                    size="sm"
                    onClick={() =>
                      updateFormik.setFieldValue("providers", [
                        ...updateFormik.values.providers,
                        emptyProvider(),
                      ])
                    }
                  >
                    <i className="mdi mdi-plus me-1" />
                    Add provider
                  </Button>
                </div>
                {typeof updateFormik.errors.providers === "string" && (
                  <div className="text-danger small mb-2">
                    {updateFormik.errors.providers}
                  </div>
                )}
                {updateFormik.values.providers.map((row, index) => (
                  <Card key={index} className="mb-2 border">
                    <CardBody className="py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="text-muted small">
                          Provider {index + 1}
                        </span>
                        {updateFormik.values.providers.length > 1 && (
                          <Button
                            type="button"
                            color="link"
                            className="text-danger p-0 small"
                            onClick={() => {
                              const next = updateFormik.values.providers.filter(
                                (_, i) => i !== index
                              );
                              updateFormik.setFieldValue(
                                "providers",
                                next.length ? next : [emptyProvider()]
                              );
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <div className="mb-2">
                        <Label className="form-label small mb-1">
                          Link <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="url"
                          name={`providers.${index}.link`}
                          placeholder="https://example.com"
                          value={row.link}
                          onChange={updateFormik.handleChange}
                          onBlur={updateFormik.handleBlur}
                          invalid={
                            updateFormik.touched.providers?.[index]?.link &&
                            !!updateFormik.errors.providers?.[index]?.link
                          }
                        />
                        {updateFormik.touched.providers?.[index]?.link &&
                          updateFormik.errors.providers?.[index]?.link && (
                            <FormFeedback type="invalid">
                              {updateFormik.errors.providers[index].link}
                            </FormFeedback>
                          )}
                      </div>
                      <div className="mb-0">
                        <Label className="form-label small mb-1">
                          Provider description
                        </Label>
                        <Input
                          type="textarea"
                          name={`providers.${index}.description`}
                          rows={2}
                          placeholder="Optional"
                          value={row.description}
                          onChange={updateFormik.handleChange}
                          onBlur={updateFormik.handleBlur}
                          invalid={
                            updateFormik.touched.providers?.[index]
                              ?.description &&
                            !!updateFormik.errors.providers?.[index]
                              ?.description
                          }
                        />
                        {updateFormik.touched.providers?.[index]?.description &&
                          updateFormik.errors.providers?.[index]
                            ?.description && (
                            <FormFeedback type="invalid">
                              {updateFormik.errors.providers[index].description}
                            </FormFeedback>
                          )}
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                color="light"
                type="button"
                onClick={() => {
                  setShowUpdateModal(false);
                  setSelectedCourse(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? "Saving…" : "Update"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default CourseList;
